import React from 'react';
import { Button } from './';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  totalItems, 
  itemsPerPage, 
  onPageChange,
  showItemsPerPage = true,
  itemsPerPageOptions = [5, 10, 25, 50]
}) => {
  // Calculate which items are currently shown
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination with ellipsis
      if (currentPage <= 3) {
        // Near the beginning
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // In the middle
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    onPageChange(1, newItemsPerPage); // Reset to first page when changing items per page
  };

  if (totalPages <= 1) {
    return null; // Don't show pagination if there's only one page or no items
  }

  return (
    <div className="flex items-center justify-between bg-surface px-4 py-3 sm:px-6">
      {/* Items info */}
      <div className="flex flex-1 justify-between sm:hidden">
        <Button
          onClick={() => onPageChange(currentPage - 1, itemsPerPage)}
          disabled={currentPage === 1}
          variant="secondary"
          size="small"
        >
          Previous
        </Button>
        <Button
          onClick={() => onPageChange(currentPage + 1, itemsPerPage)}
          disabled={currentPage === totalPages}
          variant="secondary"
          size="small"
        >
          Next
        </Button>
      </div>
      
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-text-secondary">
            Showing <span className="font-medium text-text-primary">{startItem}</span> to{' '}
            <span className="font-medium text-text-primary">{endItem}</span> of{' '}
            <span className="font-medium text-text-primary">{totalItems}</span> results
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Items per page selector */}
          {showItemsPerPage && (
            <div className="flex items-center space-x-2">
              <label htmlFor="items-per-page" className="text-sm text-text-secondary">
                Show:
              </label>
              <select
                id="items-per-page"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="rounded-md border border-border-medium bg-surface text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {itemsPerPageOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Page navigation */}
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            {/* Previous button */}
            <Button
              onClick={() => onPageChange(currentPage - 1, itemsPerPage)}
              disabled={currentPage === 1}
              variant="secondary"
              size="small"
              className="rounded-l-md"
            >
              <span className="sr-only">Previous</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
              </svg>
            </Button>
            
            {/* Page numbers */}
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span
                  key={`ellipsis-${index}`}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-text-secondary ring-1 ring-inset ring-border-medium focus:outline-offset-0"
                >
                  ...
                </span>
              ) : (
                <Button
                  key={page}
                  onClick={() => onPageChange(page, itemsPerPage)}
                  variant={currentPage === page ? "primary" : "secondary"}
                  size="small"
                  className={currentPage === page ? "z-10" : ""}
                >
                  {page}
                </Button>
              )
            ))}
            
            {/* Next button */}
            <Button
              onClick={() => onPageChange(currentPage + 1, itemsPerPage)}
              disabled={currentPage === totalPages}
              variant="secondary"
              size="small"
              className="rounded-r-md"
            >
              <span className="sr-only">Next</span>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
            </Button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
